<template>
  <div>
    <CheckoutBillingAddressForm
      :address="formValues"
      :api-errors="apiErrors"
      :countries="countries"
      :submitting="loaderStore.isEnabled"
      :with-user-information="userInformationStore.isFormRequired"
      @submit="handleSubmit"
    />

    <ReassuranceItems class="mt-24 md:mt-56" />
  </div>
</template>

<script setup lang="ts">
import { useRouter } from '#imports'
import { computed, ref } from 'vue'

import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { insertIf } from '@backmarket/utils/collection/insertIf'

import ReassuranceItems from '~/scopes/reassurance/components/ReassuranceItems/ReassuranceItems.vue'

import { CHECKOUT } from '../../routes-names'
import { useAddressStore } from '../../stores/addressStore'
import { useCartStore } from '../../stores/cartStore'
import { useLoaderStore } from '../../stores/loaderStore'
import { useUserInformationStore } from '../../stores/userInformationStore'

import CheckoutBillingAddressForm, {
  type FormValues,
} from './components/BillingAddressForm.vue'

const tracking = useTracking()
const router = useRouter()
const logger = useLogger()

const {
  market: { countryCode },
} = useMarketplace()

const cartStore = useCartStore()
const addressStore = useAddressStore()
const userInformationStore = useUserInformationStore()
const loaderStore = useLoaderStore()

const apiErrors = ref<{ [key: string]: string }>({})

const formValues = computed(() => {
  return {
    ...addressStore.billing,
    ...insertIf(userInformationStore.isFormRequired, {
      nationality: userInformationStore.nationality || countryCode,
      birthdate: userInformationStore.birthdate || '',
      nationalId: userInformationStore.nationalId,
    }),
  }
})

const countries = computed(() => {
  return [...addressStore.billableCountries]
    .map(({ name, countryCode: billableCountryCode }) => ({
      value: billableCountryCode,
      label: name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
})

const setAPIErrors = (error: { [key: string]: string }) => {
  apiErrors.value = Object.keys(error).reduce(
    (acc, key) => ({
      ...acc,
      [key]: error[key].length > 0 ? error[key][0] : '',
    }),
    {},
  )
}

const handleSubmit = async (values: FormValues) => {
  apiErrors.value = {}
  loaderStore.enable()

  try {
    tracking.trackClick({
      name: '3_billing-new-edit_cta_continue',
      zone: 'funnel',
    })

    // 1. Save address
    await addressStore.saveAddress({
      address: {
        ...addressStore.billing,
        ...values,
        countryDialInCode: addressStore.shipping.countryDialInCode,
        phone: addressStore.shipping.phone,
      },
      formType: userInformationStore.formType,
      isShipping: false,
      isBilling: true,
    })

    // 2. Reload checkout store
    await cartStore.fetchCart()
    // 3. Move to next page
    router.push({
      name: CHECKOUT.PAYMENT,
    })
  } catch (error) {
    loaderStore.disable()
    if (error instanceof Error) {
      logger.error('[CHECKOUT] Failed to submit billing form', { error })
    } else {
      // Report errors from API.
      // Ex: { postalCode: ['ZIP code is invalid'] }
      // We display only the first one
      setAPIErrors(error as { [key: string]: string })
    }
  }
}
</script>
